import { startTrappingFocus, stopTrappingFocus } from '@tesla/design-system';
import { iconClose } from '@tesla/design-system-icons';
import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../icon/icon.js';
export const Modal = ({ backdrop = true, children, className, dialogRef, disableScrollingUIAdjustments, header, onClose, onCloseAnimationFinish, open, overflowVisible = false, scrim, variant, ...rest }) => {
    const fallbackRef = useRef(null);
    const ref = dialogRef || fallbackRef;
    const headerRef = useRef(null);
    const [scrolling, setScrolling] = useState(false);
    const [hasBeenOpenedAtLeastOnce, setHasBeenOpenedAtLeastOnce] = useState(false);
    const [fullyClosed, setFullyClosed] = useState(!open);
    const onCloseTimeoutRef = useRef();
    /**
     * TODO: Modal as a whole requires a bigger refactor to allow for proper dependency checks.
     *       Ignoring eslint warnings for now.
     */
    useEffect(() => {
        if (open) {
            openModal();
        }
        else if (hasBeenOpenedAtLeastOnce) {
            closeModal();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    // Ensure cleanup runs on unMount
    useEffect(() => {
        return () => {
            closeModal();
            if (onCloseTimeoutRef.current)
                clearTimeout(onCloseTimeoutRef.current);
            setFullyClosed(true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    /**
     * This effect is concerned with adding a keyUp window event listener when
     * the modal is opened, and ensuring that the event listener is cleaned up/
     * recreated whenever any dependency changes, including the onClose prop.
     */
    useEffect(() => {
        if (!open) {
            // If the modal changes from open to closed, then the clean up function will have
            // already run (before render), and we don't need to add a new event listener
            // or register a new clean up function.
            return;
        }
        const handleKeyUp = (e) => {
            switch (e.key) {
                case 'Esc':
                case 'Escape':
                    onClose();
                    break;
                default:
                    break;
            }
        };
        window.addEventListener("keyup", handleKeyUp);
        // Note: The cleanup function runs not only during unmount, but also before
        // every re-render if any dependency has changed. This ensures that a new
        // event listener will replace a stale event listener if, for example, the
        // onClose prop changes while the modal is open!
        return () => window.removeEventListener("keyup", handleKeyUp);
    }, [open, onClose]);
    /**
     * Close
     * - Clean up active modal functionality
     */
    const closeModal = () => {
        var _a;
        // Provide a callback for when the close animation finishes running
        onCloseTimeoutRef.current = setTimeout(() => {
            setFullyClosed(true);
            if (onCloseAnimationFinish)
                onCloseAnimationFinish();
        }, 500);
        // Handle body scrolling
        if (backdrop)
            document.body.classList.remove('tds-modal--is-open');
        // Keyboard navigation
        if (ref.current)
            stopTrappingFocus(ref.current);
        // Handle possible internal scroll/overflow
        (_a = ref.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('scroll', handleScroll);
    };
    /**
     * Open
     * - Init active modal functionality
     */
    const openModal = () => {
        var _a;
        setFullyClosed(false);
        if (!hasBeenOpenedAtLeastOnce)
            setHasBeenOpenedAtLeastOnce(true);
        // Clear any remaining timeout, just in case the modal was re-opened early
        if (onCloseTimeoutRef.current)
            clearTimeout(onCloseTimeoutRef.current);
        // Handle body scrolling
        if (backdrop)
            document.body.classList.add('tds-modal--is-open');
        // Keyboard navigation
        if (ref.current)
            startTrappingFocus(ref.current);
        // Handle possible internal scroll/overflow
        (_a = ref.current) === null || _a === void 0 ? void 0 : _a.addEventListener('scroll', handleScroll);
        if (ref.current)
            ref.current.scrollTop = 0;
    };
    // @ts-expect-error - React's event types are not self-compatible in this case
    const handleScroll = (e) => {
        var _a, _b, _c, _d, _e;
        const hasEnoughSpace = (((_a = ref.current) === null || _a === void 0 ? void 0 : _a.scrollHeight) || 0) - (((_b = ref.current) === null || _b === void 0 ? void 0 : _b.offsetHeight) || 0) >
            (((_c = headerRef.current) === null || _c === void 0 ? void 0 : _c.offsetHeight) || 0);
        // To prevent jitter in the state change, only change the scrolling state
        // If it's changed by a significant amount, with a bias for it being turned off
        if (!scrolling &&
            open &&
            !disableScrollingUIAdjustments &&
            hasEnoughSpace &&
            e.currentTarget.scrollTop > (((_d = headerRef.current) === null || _d === void 0 ? void 0 : _d.offsetHeight) || 0)) {
            setScrolling(true);
        }
        else if (e.currentTarget.scrollTop < (((_e = headerRef.current) === null || _e === void 0 ? void 0 : _e.offsetHeight) || 0) / 2) {
            setScrolling(false);
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("dialog", { "aria-hidden": !open, className: cx('tds-modal', {
                [`tds-scrim--${scrim}`]: scrim,
                [`tds-modal--${variant}`]: variant,
                'tds-modal--scrolling': scrolling,
                'tds-modal-overflow--visible': overflowVisible,
            }, className), open: open, ref: ref, ...rest },
            React.createElement("div", { className: "tds-modal-header", ref: headerRef },
                React.createElement("button", { "aria-label": "Close Modal", className: "tds-modal-close", onClick: onClose, type: "button" },
                    React.createElement(Icon, { className: "tds-modal-close-icon", data: iconClose })),
                header),
            typeof children === 'function'
                ? children({ fullyClosed, hasBeenOpenedAtLeastOnce, scrolling })
                : children),
        backdrop && React.createElement("div", { className: "tds-modal-backdrop", onClick: onClose })));
};
